import { Statistics } from '../../../../redux/models/statistics/statistics';
import { MonthlyStatistic, State } from '../redux/states/dashboard-state';
import { StatisticsType } from '../../../../redux/models/statistics/statistics-type';
import { DateRange } from '../../../../redux/models/statistics/date-range';
import { StatisticsFilter } from '../../../../redux/models/statistics/statistics-filter';
import * as moment from 'moment';
import { DataPoint } from '../../../../redux/models/statistics/data-point';
import { Observable, of } from 'rxjs';
import { select, Store } from '@ngrx/store';
import {
  selectCurrentChart,
  selectOneYearAgoChart,
  selectTwoYearsAgoChart
} from '../redux/selectors/dashboard-selector';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class StatisticDataAdapter {
  readonly DATE_FORMAT = 'YYYY-MM-DD';

  constructor(private readonly store: Store<State>) {}

  adaptStatistic(
    statisticsType: StatisticsType,
    statisticsFilter: StatisticsFilter,
    currency: string
  ): Observable<Statistics[]> {
    return of(this.adaptStatisticInfo(statisticsType, statisticsFilter, currency));
  }

  adaptStatisticInfo(
    statisticsType: StatisticsType,
    statisticsFilter: StatisticsFilter,
    currency: string
  ): Statistics[] {
    const result: Statistics[] = [];

    const currentDateRange: DateRange = {
      startDate: statisticsFilter.startDate,
      endDate: statisticsFilter.endDate
    };
    this.store.pipe(select(selectCurrentChart)).subscribe((monthlyStatistics: MonthlyStatistic[]) => {
      result.push(this.getYearStatistics(currentDateRange, statisticsType, currency, monthlyStatistics));
    });

    //comparePeriodYearOffset meanings: 0 - only current year; 1 - one year ago; 2 - two years ago
    if (statisticsFilter.comparePeriodYearOffset > 0) {
      const prevYearDateRange = this.getDateRangeYearOffset(currentDateRange, -1);
      this.store.pipe(select(selectOneYearAgoChart)).subscribe((monthlyStatistics: MonthlyStatistic[]) => {
        result.push(this.getYearStatistics(prevYearDateRange, statisticsType, currency, monthlyStatistics));
      });
    }

    if (statisticsFilter.comparePeriodYearOffset > 1) {
      const twoYearsAgoDateRange = this.getDateRangeYearOffset(currentDateRange, -2);
      this.store.pipe(select(selectTwoYearsAgoChart)).subscribe((monthlyStatistics: MonthlyStatistic[]) => {
        result.push(this.getYearStatistics(twoYearsAgoDateRange, statisticsType, currency, monthlyStatistics));
      });
    }
    return result;
  }

  private getYearStatistics(
    dataRange: DateRange,
    statisticsType: StatisticsType,
    currency: string,
    monthlyStatistics: MonthlyStatistic[]
  ): Statistics {
    const currentDataPoints: DataPoint[] = [];
    for (const monthlyStatistic of monthlyStatistics) {
      currentDataPoints.push(this.getDataPoint(statisticsType, monthlyStatistic));
    }

    return {
      dateRange: dataRange,
      dataPoints: currentDataPoints,
      currency
    };
  }

  private getDateRangeYearOffset(dateRange: DateRange, offset): DateRange {
    return {
      startDate: moment(dateRange.startDate)
        .add(offset, 'years')
        .format(this.DATE_FORMAT),
      endDate: moment(dateRange.endDate)
        .add(offset, 'years')
        .format(this.DATE_FORMAT)
    };
  }

  private getDataPoint(statisticsType: StatisticsType, monthlyStatistic: MonthlyStatistic): DataPoint {
    let month = `-${monthlyStatistic.month}`;
    if (monthlyStatistic.month < 10) {
      month = `-0${monthlyStatistic.month}`;
    }
    const statisticDate = `${monthlyStatistic.year}${month}-01`;

    switch (statisticsType) {
      case StatisticsType.GROSS_SALES:
        return {
          value: monthlyStatistic.grossSales,
          date: statisticDate
        };

      case StatisticsType.NUMBER_OF_BOOKINGS:
        return {
          value: monthlyStatistic.numberOfBookings,
          date: statisticDate
        };

      case StatisticsType.REVENUES:
        return {
          value: monthlyStatistic.revenues,
          date: statisticDate
        };

      case StatisticsType.NET_REVENUES:
        return {
          value: monthlyStatistic.netRevenues,
          date: statisticDate
        };
      default:
        return {
          value: 0,
          date: statisticDate
        };
    }
  }
}
